import { useMemo } from 'react'

import {
  getCaregiverContactFromIntakeForm,
  getGuardianInfoForEpisode,
} from '@lyrahealth-inc/shared-app-logic/src/features/appointments/utils'
import {
  getProgramNameFromId,
  isChildProgram,
  isDAEngageProgram,
  isTeenProgram,
} from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'

import { useAssignmentsData } from '../lyraTherapy/assignments/data/hooks/useAssignmentsData'
import { useClientAppointmentsData } from '../lyraTherapy/clients/clientDetails/data/hooks/useClientAppointmentsData'
import { useCurrentEpisodeForClient } from '../lyraTherapy/episodes/data/hooks/useCurrentEpisodeForClient'

export const useGuardianForSelectedClient = (episodeId?: string) => {
  const { currentEpisode, loadingEpisodes } = useCurrentEpisodeForClient(episodeId)
  const { data: appointments, isLoading: loadingAppointments } = useClientAppointmentsData()
  const { data: assignments } = useAssignmentsData()

  return useMemo(() => {
    const relevantAppointments =
      (currentEpisode?.id ? appointments?.filter((a) => a.episodeId === currentEpisode.id) : appointments) ?? []

    const guardian = getGuardianInfoForEpisode(relevantAppointments, currentEpisode?.participants)
    const intakeCaregiver = getCaregiverContactFromIntakeForm(assignments, currentEpisode?.id)

    const guardianFirstName = guardian.guardianFirstName || intakeCaregiver.firstName
    const guardianLastName = guardian.guardianLastName || intakeCaregiver.lastName

    const hasGuardian = Boolean(guardianFirstName || guardianLastName)
    const guardianName = hasGuardian ? `${guardianFirstName ?? ''} ${guardianLastName ?? ''}`.trim() : ''

    const guardianAppointment = relevantAppointments.find((a) => a.guardianFirstName || a.guardianLastName)
    const programName = getProgramNameFromId(guardianAppointment?.programId)
    // DA Engage has a minor program for each of child and adolescent, and both can be caregiver-
    // addressed, so gating on isChildProgram alone silently excluded DA adolescent (LCDELIVERY-7892).
    // Teens differ from children in that they can book their own care: a teen-led teen is messaged
    // directly even when caregiver contact info is on file, so the teen branch additionally requires
    // guardianLyraId — the parent-led signal PP uses elsewhere (see
    // hasMinorGuardianContactInformation in BenefitsSummaryContainer). A child cannot self-book, so
    // the child branch does not require it.
    const isGuardianForMinor =
      hasGuardian &&
      isDAEngageProgram(programName) &&
      (isChildProgram(programName) || (isTeenProgram(programName) && Boolean(guardian.guardianLyraId)))

    return {
      guardian: { ...guardian, guardianFirstName, guardianLastName, guardianName },
      hasGuardian,
      isGuardianForMinor,
      loadingGuardian: loadingEpisodes || loadingAppointments,
    }
  }, [
    appointments,
    assignments,
    currentEpisode?.id,
    currentEpisode?.participants,
    loadingEpisodes,
    loadingAppointments,
  ])
}
